import React, { useRef, useEffect, useLayoutEffect, useCallback, useId } from 'react';

/**
 * Centralised auto-resizing textarea used for clinician notes across the
 * assessment modules (CI, SSI, DD, PAD).
 *
 * The component renders a styled container div + textarea pair.  The textarea
 * automatically grows (and shrinks) in height to fit its content, both on
 * initial data load (via useLayoutEffect) and during typing (via onInput).
 * This is necessary because the element sits inside a flex column whose
 * grow/shrink/basis classes would otherwise fight explicit height values —
 * inline style overrides (flexShrink: 0, flexBasis: 'auto') and setting
 * height to '0px' before measuring scrollHeight solve this.
 *
 * Ref forwarding is supported so parent components can hold a reference to
 * the underlying <textarea> DOM node (e.g. for focusing or scroll-into-view).
 *
 * Props:
 *   value        – controlled string value
 *   onChange      – called with the raw string (not the event)
 *   onBlur        – optional; receives the native FocusEvent (so callers can
 *                   access e.target.value if needed, or simply ignore the arg)
 *   canEdit       – (default true) toggles disabled styling + the disabled attr
 *   disabled      – optional override; when provided, takes precedence over
 *                   !canEdit for the <textarea> disabled attribute. Useful when
 *                   the disable condition is more complex than just canEdit
 *                   (e.g. SSI recording lock-out).
 *   placeholder   – optional; defaults to the canEdit-aware standard wording
 *   ariaLabel     – optional; defaults to "Clinician notes"
 *   rows          – optional; default 4
 *   minHeight     – optional; '10rem' (default), '15rem', or 'auto' (no minimum)
 *                   height on the outer container div — use 'auto' for compact
 *                   inline notes fields (e.g. MDT Plan medication rows).
 */
const DictationTextArea = React.forwardRef(({
  placeholder,
  value,
  onChange,
  onBlur,
  canEdit = true,
  disabled,
  ariaLabel = 'Clinician notes',
  rows = 4,
  minHeight = '10rem',
}, ref) => {
  const autoId = useId();
  const internalRef = useRef(null);

  // Keep refs to the latest onBlur and value so the unmount cleanup
  // (which captures the ref at mount time) always sees current data.
  const latestOnBlur = useRef(onBlur);
  const latestValue = useRef(value);
  useEffect(() => { latestOnBlur.current = onBlur; }, [onBlur]);
  useEffect(() => { latestValue.current = value; }, [value]);

  // Guard ref that prevents the unmount safety net from firing onBlur a
  // second time when it was already triggered (either by the user clicking
  // away, or by PatientDataContext's useLayoutEffect calling
  // document.activeElement.blur() on route change).  It resets whenever
  // the value changes — new edits require a fresh save opportunity.
  const hasFiredBlurRef = useRef(false);
  useEffect(() => { hasFiredBlurRef.current = false; }, [value]);

  // Wrapped handler that sets the guard before forwarding to the caller's
  // onBlur.  Used on the <textarea> instead of the raw onBlur prop.
  const handleBlur = useCallback((e) => {
    hasFiredBlurRef.current = true;
    if (onBlur) onBlur(e);
  }, [onBlur]);

  // Backup safety net: if this textarea is unmounted while still focused
  // AND onBlur has NOT already fired for the current value (e.g. a
  // conditional-render unmount that bypasses PatientDataContext's
  // route-change flush), fire the onBlur callback so the parent's save
  // handler runs.
  //
  // Note: we intentionally do NOT check internalRef.current here because
  // React clears ref callbacks (setting them to null) before running
  // useEffect cleanups, so the ref would always be null at this point.
  // latestValue.current is kept up-to-date by a separate effect above.
  useEffect(() => {
    return () => {
      if (latestOnBlur.current && !hasFiredBlurRef.current) {
        latestOnBlur.current({ target: { value: latestValue.current } });
      }
    };
  }, []);

  // Merge the forwarded ref (from parent) with our internal ref so the
  // useLayoutEffect always has access AND the parent can read/write the node.
  const setRef = useCallback((el) => {
    internalRef.current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  }, [ref]);

  // Recalculate height whenever value changes.  useLayoutEffect runs before
  // the browser paints so the user never sees the textarea at a stale height.
  useLayoutEffect(() => {
    if (internalRef.current) {
      internalRef.current.style.height = '0px';
      internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
    }
  }, [value]);

  const isDisabled = disabled !== undefined ? disabled : !canEdit;

  const resolvedPlaceholder = placeholder
    ?? (canEdit ? 'Type notes...' : 'Notes can only be added by the assigned clinician');

  const minHeightClass = minHeight === '15rem'
    ? 'min-h-[15rem]'
    : minHeight === 'auto'
      ? 'min-h-0'
      : 'min-h-[10rem]';

  return (
    <div className={`${minHeightClass} self-stretch grow shrink-0 px-3.5 py-3 ${!canEdit ? 'bg-gray-100 focus-within:ring-offset-gray-100' : 'bg-white focus-within:ring-offset-white'} rounded-lg shadow border border-gray-300 flex-col justify-end items-start gap-2 flex focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2`}>
      <textarea
        id={autoId}
        ref={setRef}
        className={`self-stretch grow shrink basis-0 text-gray-900 text-base font-normal leading-normal border-0 focus:outline-none focus:ring-0 w-full ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
        style={{ overflow: 'hidden', resize: 'none', flexShrink: 0, flexBasis: 'auto' }}
        placeholder={resolvedPlaceholder}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        onInput={(e) => { e.target.style.height = '0px'; e.target.style.height = e.target.scrollHeight + 'px'; }}
        disabled={isDisabled}
        aria-label={ariaLabel}
      />
    </div>
  );
});

DictationTextArea.displayName = 'DictationTextArea';

export default DictationTextArea;
